import React, { useEffect, useState, useContext, useRef } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";

import { ProjectApi } from "../../../api/authority/generated/index.ts";
import { GlobalContext } from "../../../utils/context/GlobalContextProvider.jsx";
import StripeCardModalWithElements from "../Subscription/StripeCardModal.jsx";

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

function CardFormContent({ project }) {
    const { t } = useTranslation();
    const { getAuthorityConfiguration, resetCurrentUserProjects } = useContext(GlobalContext);
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [paymentIntentSecret, setPaymentIntentSecret] = useState(false);
    const isFetching = useRef(false);

    useEffect(() => {
        const fetchPaymentMethod = async () => {
            if (isFetching.current) return;
            isFetching.current = true;

            try {
                const api = new ProjectApi(getAuthorityConfiguration());
                const response = await api.getPaymentMethod({
                    projectId: project.id,
                });

                if (response && response.cardNumber) {
                    setPaymentMethod(response);
                }
            } catch (error) {
                console.error("Failed to fetch payment method:", error);
                toast.error(t("subscription.failed-fetch-payment-method"));
            } finally {
                isFetching.current = false;
            }
        };

        fetchPaymentMethod();
    }, [project, getAuthorityConfiguration, t]);

    const handleEditClick = async () => {
        try {
            const api = new ProjectApi(getAuthorityConfiguration());
            const response = await api.newPaymentSetup({
                projectId: project.id,
            });

            if (response && response.setupIntentClientSecret) {
                setPaymentIntentSecret(response.setupIntentClientSecret);
                setIsEditing(true);
            }
        } catch (error) {
            console.error("Failed to fetch payment method:", error);
            toast.error(t("subscription.failed-fetch-payment-method"));
        }
    };

    const handlePaymentSuccess = () => {
        setIsEditing(false);
        resetCurrentUserProjects();
    };

    return (
        <div>
            <StripeCardModalWithElements
                isOpen={isEditing}
                onClose={() => setIsEditing(false)}
                setupIntentClientSecret={paymentIntentSecret}
                project={project}
                billingAccount={project.billingAccount}
                onPaymentSuccess={handlePaymentSuccess}
            />
            {paymentMethod ? (
                <div
                    className="p-2 rounded-lg dark:bg-gray-800 border border-gray-300 dark:border-gray-700/60 shadow-sm transition"
                >
                    <div className="pl-1 flex items-center gap-x-4">
                        {paymentMethod.brand.toLowerCase() === 'visa' ? (
                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
                                <g className="nc-icon-wrapper">
                                    <rect x="2" y="7" width="28" height="18" rx="3" ry="3" fill="#1434cb" stroke-width="0"></rect>
                                    <path
                                        d="m27,7H5c-1.657,0-3,1.343-3,3v12c0,1.657,1.343,3,3,3h22c1.657,0,3-1.343,3-3v-12c0-1.657-1.343-3-3-3Zm2,15c0,1.103-.897,2-2,2H5c-1.103,0-2-.897-2-2v-12c0-1.103.897-2,2-2h22c1.103,0,2,.897,2,2v12Z"
                                        stroke-width="0" opacity=".15"></path>
                                    <path
                                        d="m27,8H5c-1.105,0-2,.895-2,2v1c0-1.105.895-2,2-2h22c1.105,0,2,.895,2,2v-1c0-1.105-.895-2-2-2Z"
                                        fill="#fff" opacity=".2" stroke-width="0"></path>
                                    <path
                                        d="m13.392,12.624l-2.838,6.77h-1.851l-1.397-5.403c-.085-.332-.158-.454-.416-.595-.421-.229-1.117-.443-1.728-.576l.041-.196h2.98c.38,0,.721.253.808.69l.738,3.918,1.822-4.608h1.84Z"
                                        fill="#fff" stroke-width="0"></path>
                                    <path
                                        d="m20.646,17.183c.008-1.787-2.47-1.886-2.453-2.684.005-.243.237-.501.743-.567.251-.032.943-.058,1.727.303l.307-1.436c-.421-.152-.964-.299-1.638-.299-1.732,0-2.95.92-2.959,2.238-.011.975.87,1.518,1.533,1.843.683.332.912.545.909.841-.005.454-.545.655-1.047.663-.881.014-1.392-.238-1.799-.428l-.318,1.484c.41.188,1.165.351,1.947.359,1.841,0,3.044-.909,3.05-2.317"
                                        fill="#fff" stroke-width="0"></path>
                                    <path
                                        d="m25.423,12.624h-1.494c-.337,0-.62.195-.746.496l-2.628,6.274h1.839l.365-1.011h2.247l.212,1.011h1.62l-1.415-6.77Zm-2.16,4.372l.922-2.542.53,2.542h-1.452Z"
                                        fill="#fff" stroke-width="0"></path>
                                    <path fill="#fff" stroke-width="0"
                                          d="M15.894 12.624L14.446 19.394 12.695 19.394 14.143 12.624 15.894 12.624z"></path>
                                </g>
                            </svg>
                        ) : (
                            <svg className="shrink-0" width="32" height="24" xmlns="http://www.w3.org/2000/svg">
                                <defs>
                                    <linearGradient x1="1.829%" y1="100%" x2="100%" y2="2.925%" id="c1-a">
                                        <stop stop-color="#4B5563" offset="0%"></stop>
                                        <stop stop-color="#1F2937" offset="100%"></stop>
                                        <stop stop-color="#9FA1FF" offset="100%"></stop>
                                    </linearGradient>
                                </defs>
                                <g fill="none" fill-rule="evenodd">
                                    <rect fill="url(#c1-a)" width="32" height="24" rx="3"></rect>
                                    <ellipse fill="#E61C24" fill-rule="nonzero" cx="12.522" cy="12" rx="5.565"
                                             ry="5.647"></ellipse>
                                    <ellipse fill="#F99F1B" fill-rule="nonzero" cx="19.432" cy="12" rx="5.565"
                                             ry="5.647"></ellipse>
                                    <path
                                        d="M15.977 7.578A5.667 5.667 0 0 0 13.867 12c0 1.724.777 3.353 2.11 4.422A5.667 5.667 0 0 0 18.087 12a5.667 5.667 0 0 0-2.11-4.422Z"
                                        fill="#F26622" fill-rule="nonzero"></path>
                                </g>
                            </svg>
                        )}
                        <div>
                            <div
                                className="text-sm font-medium text-gray-800 dark:text-gray-100"
                            >
                                **{paymentMethod.cardNumber}
                            </div>
                        </div>
                        <div className={'ml-auto'}>
                            <button
                                className={'btn-sm bg-gray-900 text-gray-100 hover:bg-gray-800 dark:bg-gray-100 dark:text-gray-800 dark:hover:bg-white'}
                                onClick={handleEditClick}
                            >{t('subscription.edit-payment-method')}</button>
                        </div>
                    </div>
                </div>
            ) : (
                <div>{t('subscription.no-payment-method')}</div>
            )
            }
        </div>
    )
        ;
}

export default function CardForm({project}) {
    return (
        <Elements stripe={stripePromise}>
            <CardFormContent project={project} />
        </Elements>
    );
}